import React, { useEffect, useMemo, useState } from 'react';
import {
    Search,
    ArrowLeft,
    Settings
} from 'lucide-react';
import { Modal, Skeleton } from 'antd';
import dayjs from 'dayjs';
import Layout from '../../Layout'
import {
    getFbAdAccounts,
    getFbCampaigns,
    getFbAdSets,
    getFbAds,
    FbAdAccount,
    FbCampaign,
    FbAdSet,
    FbAd,
    FbInsights,
} from '../../api/FacebookAds';
import { useDebounce } from '../../hooks/useDebounce';

const PER_PAGE = 50;

// --- FORMATTERS ---

const fmtMoney = (v: any) => (v === null || v === undefined || v === '' ? '—' : `${Number(v).toLocaleString('vi-VN')} đ`);
const fmtNum = (v: any) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('vi-VN'));
const fmtPct = (v: any) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`);
const fmtDate = (v: any, withTime = true) => (v ? dayjs(v).format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') : 'N/A');

const statusVN = (s: string) => (s === 'ACTIVE' ? 'ĐANG CHẠY' : s === 'PAUSED' ? 'TẠM DỪNG' : s || '—');

const BID_STRATEGY_VN: Record<string, string> = {
    LOWEST_COST_WITHOUT_CAP: 'Thấp nhất (Tự động)',
    LOWEST_COST_WITH_BID_CAP: 'Giới hạn giá thầu',
    COST_CAP: 'Giới hạn chi phí',
};

// Lưới 12 chỉ số hiệu năng từ summary_insights của API
const buildMetrics = (ins?: FbInsights | null) => ([
    { label: 'HIỂN THỊ', value: fmtNum(ins?.impressions), color: 'bg-yellow-400' },
    { label: 'TIẾP CẬN', value: fmtNum(ins?.reach), color: 'bg-yellow-400' },
    { label: 'LƯỢT CLICK', value: fmtNum(ins?.clicks), color: 'bg-yellow-400' },
    { label: 'CHI PHÍ', value: fmtMoney(ins?.spend), color: 'bg-yellow-500', isCurrency: true },
    { label: 'CTR TRUNG BÌNH', value: fmtPct(ins?.ctr), color: 'bg-green-500' },
    { label: 'CPC TRUNG BÌNH', value: fmtMoney(ins?.cpc), color: 'bg-blue-500' },
    { label: 'CPM TRUNG BÌNH', value: fmtMoney(ins?.cpm), color: 'bg-yellow-400' },
    { label: 'TỔNG LEADS', value: fmtNum(ins?.leads), color: 'bg-green-500' },
    { label: 'CVR TRUNG BÌNH', value: fmtPct(ins?.cvr), color: 'bg-blue-500' },
    { label: 'CPL/CPA TRUNG BÌNH', value: fmtMoney(ins?.cpl), color: 'bg-red-500' },
    { label: 'DOANH THU', value: fmtMoney(ins?.revenue), color: 'bg-green-500' },
    { label: 'ROAS TRUNG BÌNH', value: ins?.roas === null || ins?.roas === undefined ? '—' : Number(ins.roas).toFixed(2), color: 'bg-yellow-500' },
]);

// Các cột ⚙ tính tại FE từ start/end/ngân sách (API chưa trả sẵn)
const adSetComputed = (set: FbAdSet) => {
    const now = dayjs();
    const start = set.start_time ? dayjs(set.start_time) : null;
    const end = set.end_time ? dayjs(set.end_time) : null;
    const daysRun = start ? `${Math.max(0, now.diff(start, 'day', true)).toFixed(1)} ngày` : '—';
    const daysRemaining = end ? (end.isBefore(now) ? 'Đã kết thúc' : `${end.diff(now, 'day', true).toFixed(1)} ngày`) : '—';
    let estimatedTotal: string = '—';
    let budgetUsedPercent: string = '—';
    if (set.daily_budget && start && end) {
        const totalDays = Math.max(0, end.diff(start, 'day', true));
        const est = Number(set.daily_budget) * totalDays;
        estimatedTotal = fmtMoney(Math.round(est));
        const spend = set.insights?.spend;
        if (spend !== undefined && spend !== null && est > 0) {
            budgetUsedPercent = `${Math.min(100, (Number(spend) / est) * 100).toFixed(0)}%`;
        }
    }
    return { daysRun, daysRemaining, estimatedTotal, budgetUsedPercent };
};

// --- COMPONENTS ---

const MetricCard = ({ label, value, color, isCurrency }: any) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 relative overflow-hidden shadow-sm flex flex-col justify-center">
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${color}`}></div>
        <div className="pl-2">
            <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
            <div className={`text-xl font-bold ${isCurrency ? 'text-[#3e91ab]' : 'text-gray-800'}`}>
                {value}
            </div>
        </div>
    </div>
);

// Vệt xám nhấp nháy thay cho spinner khi đang tải.
// Bọc trong div đặt bề rộng vì Skeleton.Input là inline-block, nhận % width không đúng;
// dùng prop `block` để vệt xám kéo đầy div bọc ngoài.
const SkeletonBar = ({ width, height = 14 }: { width: string; height?: number }) => (
    <div style={{ width }}>
        <Skeleton.Input active size="small" block style={{ height, minWidth: 0 }} />
    </div>
);

// Vài dòng skeleton chèn thẳng vào <tbody> để giữ nguyên khung bảng trong lúc chờ dữ liệu
const TableSkeletonRows = ({ colSpan, rows = 6 }: { colSpan: number; rows?: number }) => {
    const widths = ['26%', '12%', '16%', '14%', '18%', '10%'];
    return (
        <>
            {Array.from({ length: rows }).map((_, r) => (
                <tr key={r}>
                    <td colSpan={colSpan} className="px-4 py-4">
                        <div className="flex items-center gap-4">
                            {widths.map((w, c) => <SkeletonBar key={c} width={w} />)}
                        </div>
                    </td>
                </tr>
            ))}
        </>
    );
};

const StatusBadge = ({ value, vn }: { value: string; vn?: boolean }) => (
    <span className={`text-[10px] font-bold px-2 py-1 rounded shadow-sm ${value === 'ACTIVE' ? 'bg-green-100 text-green-700' : value === 'PAUSED' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
        {vn ? statusVN(value) : (value || '—')}
    </span>
);

export default function App() {
    // --- LEVEL 1: accounts + campaigns ---
    const [accounts, setAccounts] = useState<FbAdAccount[]>([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

    const [campaigns, setCampaigns] = useState<FbCampaign[]>([]);
    const [campaignsTotal, setCampaignsTotal] = useState(0);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [campPage, setCampPage] = useState(1);
    const [campSearchInput, setCampSearchInput] = useState('');
    // Lọc ngay khi gõ (không cần Enter), chờ 400ms cho ngưng gõ rồi mới gọi API
    const campSearch = useDebounce(campSearchInput.trim(), 400);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [statusCounts, setStatusCounts] = useState<{ all: number | null; active: number | null; paused: number | null }>({ all: null, active: null, paused: null });

    // --- LEVEL 2/3 ---
    const [selectedCampaign, setSelectedCampaign] = useState<FbCampaign | null>(null);
    const [selectedAdGroup, setSelectedAdGroup] = useState<FbAdSet | null>(null);

    const [adSets, setAdSets] = useState<FbAdSet[]>([]);
    const [adSetsSummary, setAdSetsSummary] = useState<FbInsights | null>(null);
    const [adSetsLoading, setAdSetsLoading] = useState(false);
    const [adGroupSearch, setAdGroupSearch] = useState('');

    const [adsList, setAdsList] = useState<FbAd[]>([]);
    const [adsSummary, setAdsSummary] = useState<FbInsights | null>(null);
    const [adsLoading, setAdsLoading] = useState(false);
    const [adSearch, setAdSearch] = useState('');

    const [jsonView, setJsonView] = useState<any>(null);

    // Nạp danh sách tài khoản QC, tự chọn tài khoản đầu tiên có chiến dịch
    useEffect(() => {
        setAccountsLoading(true);
        getFbAdAccounts({ per_page: 100 }).then((res) => {
            setAccounts(res.items);
            setAccountsLoading(false);
            if (res.items.length) {
                const first = res.items.find((a) => a.campaigns_count > 0) || res.items[0];
                setSelectedAccountId(first.id);
            }
        });
    }, []);

    // Nạp chiến dịch theo tài khoản + filter
    useEffect(() => {
        if (!selectedAccountId) return;
        setCampaignsLoading(true);
        getFbCampaigns({
            ad_account_id: selectedAccountId,
            status: statusFilter || undefined,
            search: campSearch || undefined,
            per_page: PER_PAGE,
            page: campPage,
        }).then((res) => {
            setCampaigns((prev) => (campPage === 1 ? res.items : [...prev, ...res.items]));
            setCampaignsTotal(res.total);
            setCampaignsLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccountId, statusFilter, campSearch, campPage]);

    // Đếm số chiến dịch theo trạng thái cho các nút lọc
    useEffect(() => {
        if (!selectedAccountId) return;
        setStatusCounts({ all: null, active: null, paused: null });
        Promise.all([
            getFbCampaigns({ ad_account_id: selectedAccountId, per_page: 1 }),
            getFbCampaigns({ ad_account_id: selectedAccountId, status: 'ACTIVE', per_page: 1 }),
            getFbCampaigns({ ad_account_id: selectedAccountId, status: 'PAUSED', per_page: 1 }),
        ]).then(([all, active, paused]) => {
            setStatusCounts({ all: all.total, active: active.total, paused: paused.total });
        });
    }, [selectedAccountId]);

    // Nạp nhóm QC + tổng chỉ số khi chọn chiến dịch
    useEffect(() => {
        if (!selectedCampaign) { setAdSets([]); setAdSetsSummary(null); return; }
        setAdSetsLoading(true);
        setAdGroupSearch('');
        getFbAdSets({ campaign_id: selectedCampaign.id, per_page: PER_PAGE }).then((res) => {
            setAdSets(res.items);
            setAdSetsSummary(res.summary_insights || null);
            setAdSetsLoading(false);
        });
    }, [selectedCampaign]);

    // Nạp bài QC + tổng chỉ số khi chọn nhóm QC
    useEffect(() => {
        if (!selectedAdGroup) { setAdsList([]); setAdsSummary(null); return; }
        setAdsLoading(true);
        setAdSearch('');
        getFbAds({ ad_set_id: selectedAdGroup.id, per_page: PER_PAGE }).then((res) => {
            setAdsList(res.items);
            setAdsSummary(res.summary_insights || null);
            setAdsLoading(false);
        });
    }, [selectedAdGroup]);

    // --- HANDLERS ---
    const handleSelectAccount = (accountId: number) => {
        if (accountId === selectedAccountId) return;
        setSelectedAccountId(accountId);
        setCampaigns([]);
        setCampPage(1);
        setCampSearchInput('');
        setStatusFilter('');
    };

    const handleStatusFilter = (status: string) => {
        setStatusFilter(status);
        setCampaigns([]);
        setCampPage(1);
    };

    const handleCampaignClick = (campaign: FbCampaign) => {
        setSelectedCampaign(campaign);
        setSelectedAdGroup(null);
    };

    const handleBackToCampaignList = () => {
        setSelectedCampaign(null);
        setSelectedAdGroup(null);
    };

    const handleAdGroupClick = (group: FbAdSet) => {
        setSelectedAdGroup(group);
    };

    const handleBackToAdGroups = () => {
        setSelectedAdGroup(null);
    };

    const filteredAdSets = useMemo(() => {
        const kw = adGroupSearch.toLowerCase();
        if (!kw) return adSets;
        return adSets.filter((g) => g.name.toLowerCase().includes(kw) || g.fb_ad_set_id.includes(kw));
    }, [adSets, adGroupSearch]);

    const filteredAds = useMemo(() => {
        const kw = adSearch.toLowerCase();
        if (!kw) return adsList;
        return adsList.filter((a) => a.name.toLowerCase().includes(kw) || a.fb_ad_id.includes(kw));
    }, [adsList, adSearch]);

    const campaignMetrics = buildMetrics(adSetsSummary);
    const adMetrics = buildMetrics(adsSummary);

    return (
        <Layout>

            {/* MAIN CONTENT */}
            <main className="flex-1 flex flex-col h-full overflow-hidden min-h-0">
                {/* DYNAMIC CONTENT AREA */}
                <div className="flex-1 min-h-0 overflow-hidden flex w-full">

                    {selectedAdGroup ? (
                        // ==========================================
                        // LEVEL 3: ADS DETAILS VIEW
                        // ==========================================
                        <div className="flex-1 flex flex-col h-full overflow-y-auto custom-scrollbar w-full">

                            {/* Top Header & Back Button */}
                            <div className="flex items-center justify-between mb-6 sm:flex-row flex-col gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Settings className="text-[#3e91ab]" size={24} />
                                        <h2 className="text-2xl font-bold text-gray-800 text-000000e0">Chỉ Số Hiệu Năng & Danh Sách Ads (JSON)</h2>
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Nhóm QC: <span className="font-semibold text-gray-700">{selectedAdGroup.name}</span> (ID: {selectedAdGroup.fb_ad_set_id})
                                    </div>
                                </div>
                                <button
                                    onClick={handleBackToAdGroups}
                                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 font-medium text-sm transition-colors shadow-sm"
                                >
                                    <ArrowLeft size={16} />
                                    Quay lại Nhóm QC
                                </button>
                            </div>

                            {/* Metrics Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                                {adMetrics.map((metric, index) => (
                                    <MetricCard
                                        key={index}
                                        label={metric.label}
                                        value={adsLoading ? <SkeletonBar width="70%" height={22} /> : metric.value}
                                        color={metric.color}
                                        isCurrency={metric.isCurrency}
                                    />
                                ))}
                            </div>

                            {/* Search Bar for Ads */}
                            <div className="bg-white p-4 rounded-t-xl border border-gray-200 border-b-0 shadow-sm mt-4">
                                <div className="relative w-full">
                                    <Search size={16} className="absolute left-3 top-[12px] text-gray-400" />
                                    <input
                                        type="text"
                                        value={adSearch}
                                        onChange={(e) => setAdSearch(e.target.value)}
                                        placeholder="Tìm kiếm quảng cáo theo tên hoặc ID..."
                                        className="w-full bg-white border border-gray-200 rounded-lg py-2.5 pl-9 pr-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#3e91ab] focus:ring-1 focus:ring-[#3e91ab] transition-all shadow-sm"
                                    />
                                </div>
                            </div>

                            {/* Ads Table */}
                            <div className="bg-white border border-gray-200 rounded-b-xl overflow-hidden shadow-sm flex-1">
                                <div className="h-full">
                                    <div className="overflow-x-auto custom-scrollbar h-full">
                                        <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[200px]">Tên quảng cáo</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Trạng thái</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Trạng thái thực</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">Hiển thị</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">Tiếp cận</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">Lượt Click</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">Chi phí</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">CTR ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">CPC ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">CPM ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">Leads</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">CVR ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">CPL/CPA ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">Doanh thu</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#eab308] uppercase tracking-wider">ROAS ⚙</th>
                                                    {/* New Columns */}
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Creative ID</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Khuyến nghị</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Lỗi (Issues)</th>
                                                    <th className="hidden px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_10px_rgba(0,0,0,0.05)]">Hành động</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {adsLoading && <TableSkeletonRows colSpan={19} rows={6} />}
                                                {!adsLoading && filteredAds.length === 0 && (
                                                    <tr>
                                                        <td colSpan={19} className="px-4 py-10 text-center text-gray-400 font-medium">Chưa có dữ liệu</td>
                                                    </tr>
                                                )}
                                                {!adsLoading && filteredAds.map((ad) => (
                                                    <tr key={ad.id} className="hover:bg-gray-50 transition-colors group">
                                                        <td className="px-4 py-4 sticky left-0 bg-white group-hover:bg-gray-50 z-10 border-r border-gray-50">
                                                            <div className="font-semibold text-sm text-gray-800 mb-1 whitespace-normal break-words max-w-[200px]">{ad.name}</div>
                                                            <div className="text-xs text-gray-400">ID: {ad.fb_ad_id}</div>
                                                        </td>
                                                        <td className="px-4 py-4"><StatusBadge value={ad.status} /></td>
                                                        <td className="px-4 py-4"><StatusBadge value={ad.effective_status} /></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtNum(ad.insights?.impressions)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtNum(ad.insights?.reach)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtNum(ad.insights?.clicks)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-bold text-yellow-600">{fmtMoney(ad.insights?.spend)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-green-600">{fmtPct(ad.insights?.ctr)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-blue-600">{fmtMoney(ad.insights?.cpc)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtMoney(ad.insights?.cpm)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-green-600">{fmtNum(ad.insights?.leads)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-blue-600">{fmtPct(ad.insights?.cvr)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-red-600">{fmtMoney(ad.insights?.cpl)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-semibold text-green-600">{fmtMoney(ad.insights?.revenue)}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm font-bold text-yellow-600">{ad.insights?.roas === null || ad.insights?.roas === undefined ? '—' : Number(ad.insights.roas).toFixed(2)}</span></td>

                                                        {/* New Data Cells */}
                                                        <td className="px-4 py-4"><span className="text-sm text-yellow-600">{ad.creative_id || '—'}</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm text-gray-400">—</span></td>
                                                        <td className="px-4 py-4"><span className="text-sm text-gray-400">—</span></td>

                                                        <td className="hidden px-4 py-4 sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-50 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]">
                                                            <button
                                                                onClick={() => setJsonView(ad)}
                                                                className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm transition-colors"
                                                            >View JSON</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>
                    ) : selectedCampaign ? (
                        // ==========================================
                        // LEVEL 2: AD GROUPS DETAILS VIEW
                        // ==========================================
                        <div className="flex-1 flex flex-col h-full overflow-y-auto custom-scrollbar w-full">

                            {/* Top Header & Back Button */}
                            <div className="flex items-center justify-between mb-6 sm:flex-row flex-col gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Settings className="text-[#3e91ab]" size={24} />
                                        <h2 className="text-2xl font-bold text-gray-800 text-000000e0">Nhóm Quảng Cáo & Hiệu Năng (JSON)</h2>
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Chiến dịch: <span className="font-semibold text-gray-700">{selectedCampaign.name}</span> (ID: {selectedCampaign.fb_campaign_id})
                                    </div>
                                </div>
                                <button
                                    onClick={handleBackToCampaignList}
                                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 font-medium text-sm transition-colors shadow-sm"
                                >
                                    <ArrowLeft size={16} />
                                    Quay lại danh sách chiến dịch
                                </button>
                            </div>

                            {/* Metrics Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                                {campaignMetrics.map((metric, index) => (
                                    <MetricCard
                                        key={index}
                                        label={metric.label}
                                        value={adSetsLoading ? <SkeletonBar width="70%" height={22} /> : metric.value}
                                        color={metric.color}
                                        isCurrency={metric.isCurrency}
                                    />
                                ))}
                            </div>

                            {/* Search Bar for Ad Groups */}
                            <div className="bg-white p-4 rounded-t-xl border border-gray-200 border-b-0 shadow-sm mt-4">
                                <div className="relative w-full">
                                    <Search size={16} className="absolute left-3 top-[12px] text-gray-400" />
                                    <input
                                        type="text"
                                        value={adGroupSearch}
                                        onChange={(e) => setAdGroupSearch(e.target.value)}
                                        placeholder="Tìm kiếm nhóm quảng cáo theo tên hoặc ID..."
                                        className="w-full bg-white border border-gray-200 rounded-lg py-2.5 pl-9 pr-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#3e91ab] focus:ring-1 focus:ring-[#3e91ab] transition-all shadow-sm"
                                    />
                                </div>
                            </div>

                            {/* Ad Groups Table */}
                            <div className="bg-white border border-gray-200 rounded-b-xl overflow-hidden shadow-sm flex-1">
                                <div className="h-full">
                                    <div className="overflow-x-auto custom-scrollbar h-full">
                                        <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[250px]">Tên nhóm quảng cáo</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Trạng thái</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Trạng thái thực</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Mục tiêu tối ưu</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">NS ngày</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">NS trọn đời</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">NS còn lại</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Bắt đầu</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Kết thúc</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#3e91ab] uppercase tracking-wider">% NS đã dùng ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#3e91ab] uppercase tracking-wider">Ngày đã chạy ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#3e91ab] uppercase tracking-wider">Ngày còn lại ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-[#3e91ab] uppercase tracking-wider">Tổng NS ước tính ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Hiển thị</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Tiếp cận</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Lượt Click</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Chi phí</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">CTR ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">CPC ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">CPM ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Leads</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">CVR ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">CPL/CPA ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Doanh thu</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">ROAS ⚙</th>
                                                    <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_10px_rgba(0,0,0,0.05)]">Hành động</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {adSetsLoading && <TableSkeletonRows colSpan={26} rows={6} />}
                                                {!adSetsLoading && filteredAdSets.length === 0 && (
                                                    <tr>
                                                        <td colSpan={26} className="px-4 py-10 text-center text-gray-400 font-medium">Chưa có dữ liệu</td>
                                                    </tr>
                                                )}
                                                {!adSetsLoading && filteredAdSets.map((group) => {
                                                    const computed = adSetComputed(group);
                                                    return (
                                                        <tr key={group.id} className="hover:bg-gray-50 transition-colors group">
                                                            {/* CLICKABLE NAME COLUMN */}
                                                            <td
                                                                className="px-4 py-4 sticky left-0 bg-white group-hover:bg-[#f3f7fa] z-10 border-r border-gray-50 cursor-pointer"
                                                                onClick={() => handleAdGroupClick(group)}
                                                            >
                                                                <div className="font-semibold text-sm text-[#3e91ab] group-hover:text-blue-700 mb-1 whitespace-normal break-words max-w-[250px] transition-colors">{group.name}</div>
                                                                <div className="text-xs text-gray-400">ID: {group.fb_ad_set_id}</div>
                                                            </td>
                                                            <td className="px-4 py-4"><StatusBadge value={group.status} /></td>
                                                            <td className="px-4 py-4"><StatusBadge value={group.effective_status} /></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-medium text-gray-800">{group.optimization_goal || '—'}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-600">{fmtMoney(group.daily_budget)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-600">{fmtMoney(group.lifetime_budget)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-600">—</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-600">{fmtDate(group.start_time)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-600">{group.end_time ? fmtDate(group.end_time) : '—'}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-600">{computed.budgetUsedPercent}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-medium text-amber-600">{computed.daysRun}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-500">{computed.daysRemaining}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm text-gray-600">{computed.estimatedTotal}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtNum(group.insights?.impressions)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtNum(group.insights?.reach)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtNum(group.insights?.clicks)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-bold text-yellow-600">{fmtMoney(group.insights?.spend)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-green-600">{fmtPct(group.insights?.ctr)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-blue-600">{fmtMoney(group.insights?.cpc)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-gray-800">{fmtMoney(group.insights?.cpm)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-green-600">{fmtNum(group.insights?.leads)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-blue-600">{fmtPct(group.insights?.cvr)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-red-600">{fmtMoney(group.insights?.cpl)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-semibold text-green-600">{fmtMoney(group.insights?.revenue)}</span></td>
                                                            <td className="px-4 py-4"><span className="text-sm font-bold text-yellow-600">{group.insights?.roas === null || group.insights?.roas === undefined ? '—' : Number(group.insights.roas).toFixed(2)}</span></td>
                                                            <td className="px-4 py-4 sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-50 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]">
                                                                <div className="flex gap-2">
                                                                    <button
                                                                        onClick={() => setJsonView(group)}
                                                                        className="hidden px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm transition-colors"
                                                                    >View JSON</button>

                                                                    {/* ACTION BUTTON -> GO TO ADS */}
                                                                    <button
                                                                        onClick={() => handleAdGroupClick(group)}
                                                                        className="px-3 py-1.5 text-xs font-semibold text-white bg-[#3e91ab] rounded hover:bg-blue-600 shadow-sm transition-colors"
                                                                    >
                                                                        Ads →
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>
                    ) : (
                        // ==========================================
                        // LEVEL 1: MAIN LIST VIEW (Ad Accounts & Campaigns)
                        // ==========================================
                        <div className="flex w-full h-full min-h-0 lg:flex-row flex-col">
                            {/* LEFT COLUMN: AD ACCOUNTS */}
                            <div className="lg:w-80 w-full bg-white border-r border-gray-200 flex flex-col min-h-0 shrink-0 z-10 shadow-sm rounded-xl overflow-hidden">
                                <div className="p-4 border-b border-gray-200 bg-gray-50">
                                    <h2 className="font-bold text-sm text-gray-700 uppercase tracking-wide">Tài khoản quảng cáo</h2>
                                </div>
                                <div>
                                    <div className="overflow-y-auto custom-scrollbar h-[calc(100vh-150px)]">
                                        {accountsLoading && Array.from({ length: 6 }).map((_, i) => (
                                            <div key={i} className="p-4 border-b border-gray-100 space-y-2">
                                                <SkeletonBar width="85%" />
                                                <SkeletonBar width="55%" height={12} />
                                            </div>
                                        ))}
                                        {!accountsLoading && accounts.map((account) => (
                                            <div
                                                key={account.id}
                                                onClick={() => handleSelectAccount(account.id)}
                                                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${account.id === selectedAccountId ? 'bg-[#3e91ab]/5' : ''}`}
                                            >
                                                <h3 className={`font-semibold text-sm mb-1 line-clamp-2 leading-snug ${account.id === selectedAccountId ? 'text-[#3e91ab]' : 'text-gray-800'}`}>
                                                    {account.name}
                                                </h3>
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-xs text-gray-500">ID: {account.account_id}</span>
                                                    <div className="flex gap-1.5">
                                                        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm" title="Tổng số chiến dịch">{account.campaigns_count}</span>
                                                        <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">—</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT COLUMN: CAMPAIGNS */}
                            <div className="flex-1 flex flex-col h-full overflow-hidden lg:pl-6 lg:mt-0  mt-6 w-full">

                                {/* SEARCH & FILTERS */}
                                <div className="bg-white p-4 rounded-t-xl border border-gray-200 border-b-0 shadow-sm">
                                    <div className="flex items-center gap-4 sm:flex-row flex-col">
                                        <div className="relative flex-1 sm:max-w-2xl w-full">
                                            <Search size={16} className="absolute left-3 top-[12px] text-gray-400" />
                                            <input
                                                type="text"
                                                value={campSearchInput}
                                                onChange={(e) => {
                                                    setCampaigns([]);
                                                    setCampPage(1);
                                                    setCampSearchInput(e.target.value);
                                                }}
                                                placeholder="Tìm kiếm chiến dịch theo tên hoặc ID..."
                                                className="w-full bg-white border border-gray-200 rounded-lg py-2.5 pl-9 pr-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#3e91ab] focus:ring-1 focus:ring-[#3e91ab] transition-all shadow-sm"
                                            />
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleStatusFilter('')}
                                                className={`px-4 py-2 rounded-lg transition-colors shadow-sm ${statusFilter === '' ? 'bg-[#F7C64B] text-gray-900 hover:bg-yellow-400' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                <div className={`text-sm ${statusFilter === '' ? 'font-semibold' : 'font-medium text-gray-700'}`}>Tất cả ({statusCounts.all ?? '…'})</div>
                                            </button>
                                            <button
                                                onClick={() => handleStatusFilter('ACTIVE')}
                                                className={`px-4 py-2 rounded-lg transition-colors shadow-sm ${statusFilter === 'ACTIVE' ? 'bg-[#F7C64B] text-gray-900 hover:bg-yellow-400' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                <div className={`text-sm ${statusFilter === 'ACTIVE' ? 'font-semibold' : 'font-medium text-gray-700'}`}>Đang chạy ({statusCounts.active ?? '…'})</div>
                                            </button>
                                            <button
                                                onClick={() => handleStatusFilter('PAUSED')}
                                                className={`px-4 py-2 rounded-lg transition-colors shadow-sm ${statusFilter === 'PAUSED' ? 'bg-[#F7C64B] text-gray-900 hover:bg-yellow-400' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                <div className={`text-sm ${statusFilter === 'PAUSED' ? 'font-semibold' : 'font-medium text-gray-700'}`}>Tạm dừng ({statusCounts.paused ?? '…'})</div>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* CAMPAIGNS TABLE */}
                                <div className="flex-1 bg-white border border-gray-200 rounded-b-xl overflow-hidden flex flex-col shadow-sm">
                                    <div className="flex-1 min-h-0 h-full">
                                        <div className="overflow-x-auto custom-scrollbar flex-1 h-full overflow-y-auto">
                                            <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                                                <thead className="sticky top-0 bg-gray-50 z-10 border-b border-gray-200 shadow-sm">
                                                    <tr>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-20 min-w-[280px]">Tên chiến dịch</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Trạng thái</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Trạng thái thực tế</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Mục tiêu</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Ngân sách ngày</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Ngân sách trọn đời</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Còn lại</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-normal w-24">Chiến lược giá thầu</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Loại mua</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Ngày tạo</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Cập nhật</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Bắt đầu</th>
                                                        <th className="px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Kết thúc</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {!campaignsLoading && campaigns.length === 0 && (
                                                        <tr>
                                                            <td colSpan={13} className="px-4 py-10 text-center text-gray-400 font-medium">Chưa có dữ liệu</td>
                                                        </tr>
                                                    )}
                                                    {campaigns.map((campaign) => (
                                                        <tr
                                                            key={campaign.id}
                                                            className="hover:bg-[#3e91ab]/5 cursor-pointer transition-colors group"
                                                            onClick={() => handleCampaignClick(campaign)}
                                                        >
                                                            <td className="px-4 py-4 sticky left-0 bg-white group-hover:bg-[#f3f7fa] z-10 border-r border-gray-50 transition-colors">
                                                                <div className="font-semibold text-sm text-[#3e91ab] group-hover:text-blue-700 mb-1 whitespace-normal break-words max-w-[250px]">{campaign.name}</div>
                                                                <div className="text-xs text-gray-400">ID: {campaign.fb_campaign_id}</div>
                                                            </td>
                                                            <td className="px-4 py-4"><StatusBadge value={campaign.status} vn /></td>
                                                            <td className="px-4 py-4"><StatusBadge value={campaign.effective_status} vn /></td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm font-medium text-amber-600">{campaign.objective || '—'}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm font-semibold text-gray-800">{fmtMoney(campaign.daily_budget)}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm text-gray-600">{fmtMoney(campaign.lifetime_budget)}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm font-medium text-[#3e91ab]">{fmtMoney(campaign.budget_remaining)}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <div className="text-sm text-gray-600 whitespace-normal w-24 leading-snug">{campaign.bid_strategy ? (BID_STRATEGY_VN[campaign.bid_strategy] || campaign.bid_strategy) : '—'}</div>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm text-gray-600">{campaign.buying_type || '—'}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm text-gray-600">{fmtDate(campaign.created_at, false)}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm text-gray-600">{fmtDate(campaign.updated_at)}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm text-gray-600">{fmtDate(campaign.start_time)}</span>
                                                            </td>
                                                            <td className="px-4 py-4">
                                                                <span className="text-sm text-gray-600">{fmtDate(campaign.stop_time)}</span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {/* Trang sau nối tiếp bên dưới nên skeleton đặt cuối danh sách */}
                                                    {campaignsLoading && <TableSkeletonRows colSpan={13} rows={6} />}
                                                </tbody>
                                            </table>
                                            {!campaignsLoading && campaigns.length < campaignsTotal && (
                                                <div className="py-3 text-center border-t border-gray-100">
                                                    <button
                                                        onClick={() => setCampPage((p) => p + 1)}
                                                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                                                    >
                                                        Tải thêm ({campaigns.length}/{campaignsTotal})
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Modal xem JSON gốc của bản ghi */}
            <Modal
                open={!!jsonView}
                onCancel={() => setJsonView(null)}
                footer={null}
                width={720}
                title={`JSON: ${jsonView?.name || ''}`}
            >
                <pre className="max-h-[60vh] overflow-auto bg-slate-900 text-green-300 text-xs rounded-lg p-4">
                    {jsonView ? JSON.stringify(jsonView, null, 2) : ''}
                </pre>
            </Modal>

            <style dangerouslySetInnerHTML={{
                __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #CBD5E1;
          border-radius: 20px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background-color: #94A3B8;
        }
      `}} />
        </Layout>
    );
}
